import { AxiosError } from 'axios';
import { createStore, useStore } from 'zustand';

import { fetchAuthToken } from '@lib/api';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { trackEvent } from '@utils/analytics';
import { getData, removeData, setData } from '@lib/storage';

import { UserLoginPayload } from './types';

export type AuthValue = {
  initialised: boolean;
  loading: boolean;
  error: AxiosError | null;
  isLoggedInOnElmenus: boolean;
  isLoggedInOnFirebase: boolean;
  init: () => Promise<void>;
  login: (credentials: UserLoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  computed: {
    isLoggedIn: boolean;
  };
};

export const authStore = createStore<AuthValue>((set, get) => ({
  initialised: false,
  loading: false,
  error: null,
  isLoggedInOnElmenus: false,
  isLoggedInOnFirebase: false,
  init: async () => {
    const [accessToken, refreshToken, firebaseCustomToken] = await Promise.all([
      getData('accessToken'),
      getData('refreshToken'),
      getData('firebaseCustomToken'),
    ]);

    if (accessToken && refreshToken && firebaseCustomToken) {
      try {
        await firebase.loginToFirebase();

        set({
          isLoggedInOnElmenus: true,
          isLoggedInOnFirebase: true,
        });
      } catch {
        await get().logout();
      } finally {
        set({
          initialised: true,
        });
      }
    } else {
      set({
        isLoggedInOnElmenus: false,
        isLoggedInOnFirebase: false,
        initialised: true,
      });
    }
  },
  login: async (credentials: UserLoginPayload) => {
    try {
      set({ loading: true, error: null });

      // 1. login to elmenus
      const { accessToken, refreshToken } = await fetchAuthToken({
        password: credentials.password,
        userName: credentials.username,
      });

      // 2. store authToken/refreshToken in local storage
      await Promise.all([
        setData('accessToken', accessToken),
        setData('refreshToken', refreshToken),
      ]);
      set({ isLoggedInOnElmenus: true });

      // 3. login to firebase using stored authToken
      await firebase.loginToFirebase();
      set({ isLoggedInOnFirebase: true });

      trackEvent('Agent Login');

      // in case of error, let's store the error and log user out to start over
    } catch (error) {
      const axiosError = error as AxiosError;
      logger.error('Error during login:', axiosError);
      set({ error: axiosError });
      await get().logout();

      // unset loading state in any case
    } finally {
      set({ loading: false });
    }
  },
  logout: async () => {
    set({ isLoggedInOnElmenus: false, isLoggedInOnFirebase: false });

    await Promise.all([
      removeData('accessToken'),
      removeData('refreshToken'),
      removeData('firebaseCustomToken'),
    ]);

    trackEvent('Agent Logout');
  },
  computed: {
    get isLoggedIn() {
      return get().isLoggedInOnElmenus && get().isLoggedInOnFirebase;
    },
  },
}));

authStore.getState().init();

export const useAuthStore = () => useStore(authStore);
